/* eslint-disable no-param-reassign */
/* eslint-disable no-unused-expressions */
import { scrollToAnchor } from 'js/ra.js';
import * as LS from '../../../../locator-shared.js';
import * as FS from '../../../../../common/filters/filter-shared.js';
import { handleModalClose, openModal } from '../../../../../content/modal-container/clientlibs/site/js/modal-container.js';
import { createFilledElement } from '../../../../../common/filters/fill-template.js';

const componentName = 'locator-search';

class LocatorSearch extends HTMLElement {

    // ==========
    // COMPONENT SETUP
    // ==========
    selectors = {
        locationAutosuggestItem: `.${componentName}__location-autosuggest-item`,
        selectedLocationAutosuggestItem: `.${componentName}__location-autosuggest-item[aria-selected="true"]`,
        resultLogo: `.${componentName}__result-logo`,
        resultChipsContainer: `.${componentName}__result-chips-container`,
        resultIconPhone: `.${componentName}__result-icon-phone`,
        resultIconEmail: `.${componentName}__result-icon-email`,
    };

    elements = {
        // Config
        detailLink: this.querySelector(`#${componentName}__detail-link`),
        salesOfficeLogo: this.querySelector(`#${componentName}__sales-office-logo`),
        // Location Select
        locationSelectButton: this.querySelector(`.${componentName}__location-select-button`),
        locationSelectText: this.querySelector(`.${componentName}__location-select-text`),
        locationDialog: this.querySelector(`.${componentName}__location-dialog`),
        locationDialogCard: this.querySelector(`.${componentName}__location-dialog-card`),
        locationDialogClose: this.querySelector(`.${componentName}__location-dialog-close`),
        locationSearchInput: this.querySelector('input[name="loca"]'),
        locationAutosuggestList: this.querySelector(`.${componentName}__location-autosuggest-list`),
        locationAutosuggestItemTemplate: this.querySelector(`#${componentName}__location-autosuggest-item-template`),
        // Filter
        filterDialog: this.querySelector(FS.selectors.dialog),
        filterMobileShow: this.querySelector(FS.selectors.mobileShow),
        filterMobileClose: this.querySelector(FS.selectors.mobileClose),
        filterMobileSubmit: this.querySelector(FS.selectors.mobileSubmit),
        filterForm: this.querySelector(FS.selectors.form),
        filterResets: this.querySelectorAll(FS.selectors.resets),
        filterDetails: this.querySelectorAll(FS.selectors.details),
        facetSearches: this.querySelectorAll(FS.selectors.facetSearches),
        checkboxItemTemplate: this.querySelector(`.${componentName}__checkbox-item-template`),
        tooltips: this.querySelectorAll(FS.selectors.tooltips),
        // Filter chips
        filterMobileHeaderBottom: this.querySelector(FS.selectors.mobileHeaderBottom),
        filterChipsContainers: this.querySelectorAll(FS.selectors.chipsContainers),
        filterChipTemplate: this.querySelector(FS.selectors.chipTemplate),
        // Results
        desktopSortSelect: this.querySelector(FS.selectors.desktopSortSelect),
        resultsContentContainer: this.querySelector(FS.selectors.resultsContentContainer),
        noResults: this.querySelector(`.${componentName}__no-results`),
        resultCardTemplate: this.querySelector(`#${componentName}__result-card-template`),
        resultChipTemplate: this.querySelector(`#${componentName}__result-chip-template`),
        // Pagination
        paginationForm: this.querySelector(FS.selectors.paginationForm),
    };

    config = {
        locationStorageKey: 'ra_locator_search_location',
        defaultNumResults: 12,
        querySearchParam: 'q',
        indexSearchParam: 'i',
        locationSearchParam: 'loca',
        sortSearchParam: 'sort',
        numResultsParam: 'numResults',
        wcmmodeParam: 'wcmmode',
        locatorDetailLink: this.elements.detailLink.href,
        salesOfficeLogoSrc: this.elements.salesOfficeLogo.dataset.src,
        noLocationText: this.elements.locationSelectText.textContent,
    };

    state = {
        // WCM mode
        wcmmode: new URL(window.location.href).searchParams.get(this.config.wcmmodeParam),
        // UI information
        resultCount: Infinity,
        areFiltersInitialized: false,
        // API request information
        filterParams: Object.fromEntries(Object.entries(LS.FilterFacets).map(([key]) => [key, []])), // Value arrays
        queryText: null,
        resultStartIndex: 0,
        sort: null,
        numResults: this.config.defaultNumResults,
        userLocation: {
            name: null, latitude: null, longitude: null, country: null, countrySubdivisionName: null, municipality: null
        },
        location: {
            name: null, latitude: null, longitude: null, country: null, countrySubdivisionName: null, municipality: null
        },
        // API request helpers
        aprCodeCache: new Map(),
        abortController: new AbortController(),
        // Location autosuggest
        autosuggestManager: null,
        autosuggestTimer: null,
    };

    // ==========
    // GET DATA
    // ==========
    runSearch = async (initialLoad = false) => {
        window.parent.dispatchEvent(new CustomEvent('show-loading'));

        // Abort any running request
        this.state.abortController.abort();
        this.state.abortController = new AbortController();
        const { signal } = this.state.abortController;

        if (initialLoad) {
            this.elements.noResults.remove();
            this.state.userLocation = await LS.readUserLocation(true);
            this.state.location = await LS.readStoredLocation();
            this.elements.locationSearchInput.value = this.state.location.name ?? '';
            await this.readUrlParams();
        } else {
            await this.readFilterValues();
            this.writeUrlParams();
        }

        const mostRecentGeoPoint = await this.readLocationStateString(); // Save geoPoint for APR code caching

        // If distributor is selected, location must be present
        if (mostRecentGeoPoint == null && this.state.filterParams.ptyp.some((type) => type.includes('Distributor'))) {
            this.openLocationDialog();
            return;
        }

        // Attempt to fetch data up to 3 times
        const apiSearchParams = await this.generateApiParams();
        let data;
        for (let attempt = 0; attempt < 3; attempt += 1) {
            try {
                data = await LS.fetchData(apiSearchParams, { signal }); // eslint-disable-line no-await-in-loop
            } catch (error) {
                if (error.name === 'AbortError') { // Indicates that another search was started
                    return;
                }
            }
            if (data) {
                break;
            }
        }

        // Update result Count
        this.state.resultCount = data?.response.numFound ?? 0;

        // Cache APR code
        this.cacheAprCode(mostRecentGeoPoint, this.getAprCode(data));

        // Populate filters on initial load
        if (data && !this.state.areFiltersInitialized) {
            this.populateFilterOptions(data);
            await new Promise((resolve) => { // Wait for filters to finish populating
                window.setTimeout(() => {
                    FS.showFilterSearchFields(this.elements.filterDetails);
                    this.writeFilterValues();
                    resolve();
                });
            });
            this.state.areFiltersInitialized = true;
        }

        // Display results
        if (this.state.resultCount === 0) {
            this.displayNoResults();
        } else {
            await this.displayResults(data);
            this.updateFilterCounts(data);
        }
        this.updateUiState();

        if (!initialLoad) {
            scrollToAnchor(this);
        }

        window.parent.dispatchEvent(new CustomEvent('hide-loading'));
    };

    // ==========
    // Generate API search params from saved state
    // ==========
    generateApiParams = async () => {
        /* Query format example: If you have selected "type": "Option 1" and "Option 2",
           then generate: "type:("Option 1"OR"Option 2")" */
        const facets = Object.entries(this.state.filterParams)
            .map(([fieldName, filterValueArray]) => [
                LS.FilterFacets[fieldName], // Map to API field name
                filterValueArray.map((value) => `"${value}"`).join('OR')
            ])
            .filter(([, valuesString]) => valuesString !== '')
            .map(([apiFieldName, valuesString]) => `${apiFieldName}:(${valuesString})`)
            .join(';');

        /* Multi-word searches should only match results that contain all of the entered words.
           To do so, replace spaces in unquoted text with " AND ". */
        const queryText = this.state.queryText
            ?.split('"')
            .map((subString, index) => (index % 2 ? subString : subString.replaceAll(' ', ' AND ')))
            .join('"') ?? '';

        const countryMap = {
            'Trinidad & Tobago': 'Trinidad,Tobago',
            'United States': 'USA',
            'Hong Kong SAR': 'Hong Kong',
            'South Korea': 'Korea',
            'Russian Federation': 'Russian Fed.',
            Türkiye: 'TURKIYE',
            'United Arab Emirates': 'Utd.Arab Emir.',
        };

        const searchParams = {
            queryText,
            facets,
            startIndex: this.state.resultStartIndex,
            numResults: this.state.numResults,
            sort: this.state.sort ?? 'dist',
            geoPoint: await this.readLocationStateString(),
            country: countryMap[this.state.location.country] ?? this.state.location.country
        };

        if (searchParams.geoPoint == null) {
            // Show no distributor results if there is no geoPoint
            searchParams.aprCode = -1;
        } else if (this.state.aprCodeCache.has(searchParams.geoPoint)) {
            // Use cached APR code in query if one matches the geoPoint
            searchParams.aprCode = this.state.aprCodeCache.get(searchParams.geoPoint);
        }

        return searchParams;
    };

    /** @returns {{ latitude: number, longitude: number }?} */
    readLocationState = async () => {
        // Location search falls back to userLocation
        let location = { name: null, latitude: null, longitude: null };
        if (this.state.location.latitude != null && this.state.location.longitude != null) {
            location = this.state.location;
        } else if (this.state.userLocation.latitude != null && this.state.userLocation.longitude != null) {
            location = this.state.userLocation;
        }

        if (location.name != null && location.name !== '') {
            this.elements.locationSelectText.textContent = location.name;
        } else {
            this.elements.locationSelectText.textContent = this.config.noLocationText;
        }

        return location.latitude != null && location.longitude != null ? location : null;
    };

    /** @returns {string?} */
    readLocationStateString = async () => {
        const location = await this.readLocationState();
        return location ? `${location.latitude},${location.longitude}` : null;
    };

    /**
     * Cache APR code for use when same geoPoint is passed
     * @param {string?} geoPoint
     * @param {string?} aprCode
     */
    cacheAprCode = (geoPoint, aprCode) => {
        if (geoPoint && aprCode) {
            this.state.aprCodeCache.set(geoPoint, aprCode);
        }
    };

    /**
     * @param {Object} data
     * @returns {string?}
     */
    getAprCode = (data) => {
        const fqField = [data.responseHeader?.params?.fq].flat(); // fq field can be either a string or an array, always grab it as an array
        const aprCodeField = fqField.find((str) => String(str).startsWith('rl_apr_code'));
        const aprCode = aprCodeField // extract 'TH1' from 'rl_apr_code_ss:("n/a" or "TH1")'
            ?.match(/\((.*)\)/)?.[1] // returns '"n/a" or "TH1"'
            .split('or') // returns ['"n/a" ', ' "TH1"']
            .map((value) => value.match(/"(.*)"/)?.[1]) // returns ["n/a", "TH1"]
            .filter((value) => value != null && value !== 'n/a')?.[0]; // returns "TH1"
        return aprCode;
    };

    // ==========
    // Read filter values into saved state and write filter values out from saved state
    // ==========
    /**
     * Update variables in this.state based on the contents of multiMap
     * @param {URLSearchParams | FormData} multiMap
     */
    updateSearchParams = async (multiMap) => {
        // Special parameters
        const indexSearchParamValue = multiMap.get(this.config.indexSearchParam);
        if (indexSearchParamValue != null) {
            const newStartIndex = Number(indexSearchParamValue);
            if (Number.isInteger(newStartIndex)) {
                this.state.resultStartIndex = newStartIndex;
            }
        }
        this.state.queryText = multiMap.get(this.config.querySearchParam);
        this.state.sort = multiMap.get(this.config.sortSearchParam);
        this.state.numResults = Number(multiMap.get(this.config.numResultsParam) ?? this.config.defaultNumResults);

        // Filter facets
        Object.keys(this.state.filterParams).forEach((key) => {
            this.state.filterParams[key] = multiMap.getAll(key).filter((value) => value !== '');
        });

        // Location
        const newLocationName = multiMap.get(this.config.locationSearchParam);
        if (newLocationName == null) {
            // do nothing - this is the case when reading URL params with no location specified
        } else if (newLocationName === '') {
            this.updateLocationState('', null, null);
        } else if (newLocationName !== this.state.location.name // location was changed
            || this.state.location.latitude == null || this.state.location.longitude == null) { // location did not have lat/long
            const {
                latitude, longitude, country, countrySubdivisionName, municipality
            } = await this.getCoordinates(newLocationName);
            this.updateLocationState(newLocationName, latitude, longitude, country, countrySubdivisionName, municipality);
        }

    };
    /** Update variables in this.state based on the URL parameters */
    readUrlParams = () => this.updateSearchParams(new URLSearchParams(window.location.search));
    /** Update variables in this.state based on the on-page form contents */
    readFilterValues = () => this.updateSearchParams(new FormData(this.elements.filterForm));

    /**
     * Generate full set of search params including this.state.filterParams and
     * one-off params like index, query, sort, location
     * @returns {[string, string | number][]} Array of entries `[ paramName, paramValue ]`
    */
    generateSearchParamsEntries = () => {
        const entries = [
            [this.config.wcmmodeParam, this.state.wcmmode],
            [this.config.indexSearchParam, this.state.resultStartIndex],
            [this.config.querySearchParam, this.state.queryText],
            [this.config.sortSearchParam, this.state.sort],
            [this.config.locationSearchParam, this.state.location.name],
        ];
        Object.entries(this.state.filterParams).forEach(([filterName, filterValueArray]) => {
            filterValueArray.forEach((filterValue) => entries.push([filterName, filterValue]));
        });
        return entries.filter(([, value]) => value);
    };

    /** Update URL parameters based on contents of this.state */
    writeUrlParams = () => {
        const urlParams = new URLSearchParams(this.generateSearchParamsEntries());
        const newUrl = new URL(window.location.href);
        newUrl.search = urlParams.toString();
        window.history.replaceState({}, '', newUrl);
        sessionStorage.setItem(LS.backLinkSessionStorageKey, newUrl);
    };

    /** Fill the contents of the on-page form elements based on the contents of this.state */
    writeFilterValues = () => {
        this.generateSearchParamsEntries().forEach(([key, value]) => {
            FS.fillInput([...this.elements.filterForm.elements], key, value, true);
        });
    };

    // ==========
    // Update filter contents based on received data
    // ==========
    /**
     * Populate filters by adding facet value options
     * @param {Object} data
     */
    populateFilterOptions = (data) => {
        const apiFacetFields = data.facet_counts.facet_fields; // Array of facets received
        Object.entries(LS.FilterFacets).forEach(([fieldName, apiFieldName]) => {
            const checkboxList = this.querySelector(`${FS.selectors.checkboxList}[data-name="${fieldName}"]`);
            if (checkboxList && checkboxList.children.length === 0) {
                const checkboxItems = apiFacetFields[apiFieldName] // E.g. ["SI", 2204, "OEM", 1873, "Education", 592] (alternating values and counts)
                    ?.filter((element) => typeof element === 'string') // Filter out counts
                    .sort(new Intl.Collator().compare) // Case insensitive sort
                    .map((optionValue) => createFilledElement(
                        this.elements.checkboxItemTemplate,
                        'data-xt',
                        { name: fieldName, value: optionValue, label: optionValue }
                    ));
                if (checkboxItems && checkboxItems.length > 0) {
                    checkboxList.replaceChildren(...checkboxItems);
                }
            }
        });
    };

    /** @param {Object} data */
    updateFilterCounts = (data) => {
        const apiFacetFields = data.facet_counts.facet_fields;

        // Checkbox counts
        Object.entries(LS.FilterFacets).forEach(([fieldName, apiFieldName]) => {
            const checkboxList = this.elements.filterForm.querySelector(`${FS.selectors.checkboxList}[data-name="${fieldName}"]`);
            const facetList = apiFacetFields[apiFieldName];
            if (checkboxList && facetList) {
                for (let i = 0; i < facetList.length; i += 2) {
                    const value = facetList[i];
                    const valueCount = facetList[i + 1];
                    const checkbox = checkboxList.querySelector(`input[type="checkbox"][value="${value}"]`);
                    if (checkbox) {
                        FS.updateFacetItemCount(checkbox, valueCount, fieldName === 'ptyp');
                    }
                }
            }
        });

        // hide facets with zero results
        FS.hideFacetsWithNoResults(this.elements.filterDetails);
    };

    // ==========
    // LOCATION SEARCH
    // ==========
    /**
     * @param {string?} name
     * @param {number?} latitude
     * @param {number?} longitude
     * @param {string?} country
     * @param {string?} countrySubdivisionName
     * @param {string?} municipality
     */
    updateLocationState = (name, latitude, longitude, country = null, countrySubdivisionName = null, municipality = null) => {
        this.state.location = {
            name, latitude, longitude, country, countrySubdivisionName, municipality
        };
        LS.writeStoredLocation(this.state.location);
    };

    /** @param {string} query */
    doAutosuggest = async (query) => {
        if (query != null && query !== '') {

            const apiUrl = new URL('https://atlas.microsoft.com/search/address/json');
            apiUrl.searchParams.set('subscription-key', LS.getConfig().bingMapsKey);
            apiUrl.searchParams.set('query', query);
            apiUrl.searchParams.set('limit', 5);
            apiUrl.searchParams.set('api-version', '1.0');
            apiUrl.searchParams.set('typeahead', 'false');
            apiUrl.searchParams.set('entityType', 'Municipality,MunicipalitySubdivision,Neighbourhood,PostalCodeArea');
            apiUrl.searchParams.set('language', 'en-US');

            try {
                const response = await fetch(apiUrl);
                const data = await response.json();
                const autosuggestItems = data.results
                    .map((result, index) => {
                        const { address, position } = result;
                        return createFilledElement(this.elements.locationAutosuggestItemTemplate, 'data-xt', {
                            title: address.freeformAddress || '',
                            subtitle: `${address.municipality || ''}, ${address.countrySubdivisionCode || ''}`,
                            suggestion: address.freeformAddress || '',
                            latitude: position.lat,
                            longitude: position.lon,
                            country: address.country || '',
                            countrySubdivisionName: address.countrySubdivisionName || address.countrySubdivisionCode || '',
                            municipality: address.municipality || '',
                            selected: index === 0,
                        }, { keepIfEmpty: true });
                    });

                this.elements.locationAutosuggestList.replaceChildren(...autosuggestItems);
                this.elements.locationAutosuggestList.hidden = autosuggestItems.length < 1;
            } catch (error) {
                console.error('Azure Maps autosuggest error:', error); // eslint-disable-line no-console
                this.elements.locationAutosuggestList.replaceChildren();
                this.elements.locationAutosuggestList.hidden = true;
            }
        } else {
            this.elements.locationAutosuggestList.replaceChildren();
            this.elements.locationAutosuggestList.hidden = true;
        }
    };

    /** @param {HTMLElement?} autosuggestItem */
    focusAutosuggestItem = (autosuggestItem) => {
        if (autosuggestItem) {
            const siblings = autosuggestItem.parentElement.children;
            siblings.forEach((sibling) => sibling.setAttribute('aria-selected', sibling === autosuggestItem));
        }
    };

    /** @param {HTMLElement?} autosuggestItem */
    selectAutosuggestItem = (autosuggestItem) => {
        if (autosuggestItem) {
            const {
                suggestion, latitude, longitude, country, countrySubdivisionName: initialCountrySubdivisionName, municipality
            } = autosuggestItem.dataset;

            let countrySubdivisionName = initialCountrySubdivisionName;
            if ((typeof countrySubdivisionName === 'undefined' || !countrySubdivisionName) && (typeof autosuggestItem.dataset.countrysubdivisionname === 'string' && autosuggestItem.dataset.countrysubdivisionname !== '')) {
                countrySubdivisionName = autosuggestItem.dataset.countrysubdivisionname;
            }

            this.elements.locationSearchInput.value = suggestion;
            this.updateLocationState(suggestion, latitude, longitude, country, countrySubdivisionName, municipality);

            this.elements.locationAutosuggestList.hidden = true;
            this.elements.locationAutosuggestList.replaceChildren();

            this.handleFilterForm();
            this.closeLocationDialog();
        }
    };

    selectFocusedAutosuggestItem = () => {
        this.elements.locationAutosuggestList.hidden = true;
        /* If what's in the search box does not match what was selected from
           the autosuggest dropdown, then either autocomplete or clear the
           search box. */
        if (this.elements.locationSearchInput.value !== this.state.location.name) {
            const autosuggestItem = this.elements.locationAutosuggestList.querySelector(this.selectors.selectedLocationAutosuggestItem);
            if (autosuggestItem) {
                this.selectAutosuggestItem(autosuggestItem);
            } else {
                this.elements.locationSearchInput.value = '';
            }
        }
    };

    /** @param {InputEvent} ev */
    handleLocationInput = (ev) => {
        window.clearTimeout(this.state.autosuggestTimer);
        const { value } = ev.currentTarget;
        this.state.autosuggestTimer = window.setTimeout(() => {
            this.doAutosuggest(value);
        }, 250);
    };

    /** @param {KeyboardEvent} ev */
    handleLocationKeys = (ev) => {
        const selectedAutosuggest = this.elements.locationAutosuggestList.querySelector(this.selectors.selectedLocationAutosuggestItem);
        if (selectedAutosuggest) {
            let isHandled = true;
            switch (ev.key) {
            case 'ArrowDown':
                this.focusAutosuggestItem(selectedAutosuggest.nextElementSibling);
                break;
            case 'ArrowUp':
                this.focusAutosuggestItem(selectedAutosuggest.previousElementSibling);
                break;
            case 'Enter':
                this.selectAutosuggestItem(selectedAutosuggest);
                break;
            default:
                isHandled = false;
                break;
            }
            if (isHandled) {
                ev.preventDefault();
            }
        }
    };

    /** @param {MouseEvent} ev */
    handleAutosuggestClick = (ev) => {
        ev.preventDefault();
        const autosuggestItem = ev.target.closest(this.selectors.locationAutosuggestItem);
        if (autosuggestItem) {
            this.selectAutosuggestItem(autosuggestItem);
        }
    };

    handleLocationBlur = () => {
        // Wait for click handler to finish in case autosuggest item was clicked
        window.setTimeout(() => {
            this.selectFocusedAutosuggestItem();
        }, 200);
    };

    /**
     * @param {string} query
     * @returns {Promise<{ latitude: number?, longitude: number?, country: string?, countrySubdivisionName: string?, municipality: string? }>}
     */
    getCoordinates = async (query) => {
        // The Microsoft.Maps.Search module only works if you have a map on the
        // page, which is why the REST endpoint is used here instead.
        const apiUrl = new URL('https://atlas.microsoft.com/search/address/json');
        apiUrl.searchParams.set('subscription-key', LS.getConfig().bingMapsKey);
        apiUrl.searchParams.set('query', query);
        apiUrl.searchParams.set('limit', 1);
        apiUrl.searchParams.set('api-version', '1.0');

        const response = await fetch(apiUrl);
        const json = await response.json();
        const result = json?.results?.[0];
        const coordinates = result?.position;
        const address = result?.address;

        let latitude = null;
        let longitude = null;
        let country = null;
        let countrySubdivisionName = null;
        let municipality = null;

        if (coordinates) {
            latitude = coordinates.lat;
            longitude = coordinates.lon;
        }

        if (address) {
            country = address.country || '';
            countrySubdivisionName = address.countrySubdivisionName || address.countrySubdivisionCode || '';
            municipality = address.municipality || '';
        }

        return {
            latitude, longitude, country, countrySubdivisionName, municipality
        };
    };

    openLocationDialog = () => {
        window.parent.dispatchEvent(new CustomEvent('hide-loading'));
        openModal(this.elements.locationDialog);
    };

    closeLocationDialog = () => {
        this.elements.locationDialog.close();
        handleModalClose();
    };

    // ==========
    // DISPLAY RESPONSE DATA
    // ==========
    displayNoResults = () => {
        this.elements.noResults.hidden = false;
        this.elements.resultsContentContainer.replaceChildren(this.elements.noResults);
    };

    /** @param {Object} data */
    displayResults = async (data) => {
        const aprCode = this.getAprCode(data);
        const resultCards = await Promise.all(data.response.docs.map((doc) => this.createResultCard(doc, aprCode)));
        this.elements.resultsContentContainer.replaceChildren(...resultCards);
    };

    /** @param {Object} doc */
    generateCardBodyText = (doc) => {
        // Fields that should be counted in the body text
        const bodyFields = [
            { key: LS.Fields.Disciplines, singular: 'Capability', plural: 'Capabilities' },
            // DMX-2417: Do not display industries on technology partner cards
            LS.getField(doc, LS.Fields.PartnerType) === LS.PartnerTypes.TechnologyPartner
                ? null
                : { key: LS.Fields.Industries, singular: 'Industry', plural: 'Industries' },
            { key: LS.Fields.MachineSegments, singular: 'Machine Segment', plural: 'Machine Segments' },
            { key: LS.Fields.MachineSubSegments, singular: 'Machine Sub-Segment', plural: 'Machine Sub-Segments' },
            { key: LS.Fields.Locations, singular: 'Location', plural: 'Locations' },
            { key: LS.Fields.Certifications, singular: 'Third-party Certification', plural: 'Third-party Certifications' },
        ].filter((obj) => obj != null);

        /**
         * @param {string} key
         * @param {string} singular
         * @param {string} plural
         */
        const getCountString = (key, singular, plural) => {
            const count = LS.getFieldAsArray(doc, key)?.length;
            if (count > 1) {
                return `${count} ${plural}`;
            } if (count === 1) {
                return `${count} ${singular}`;
            }
            return null;
        };

        return bodyFields
            .map(({ key, singular, plural }) => getCountString(key, singular, plural))
            .filter((countString) => countString != null)
            .join(', ');
    };

    /**
     * @param {Object} doc
     * @param {string?} aprCode
     */
    createResultCard = async (doc, aprCode = null) => {
        const partnerType = LS.getField(doc, LS.Fields.PartnerType);
        const isOpCo = (partnerType === LS.PartnerTypes.Distributor)
            && (aprCode != null)
            && LS.getFieldAsArray(doc, LS.Fields.Distributor.AprCode).includes(aprCode);

        // Build link to detail page
        const partnerLinkParams = new URLSearchParams();
        if (this.state.wcmmode) {
            partnerLinkParams.set(this.config.wcmmodeParam, this.state.wcmmode);
        }
        partnerLinkParams.set(LS.OtherUrlParams.accountId, LS.getField(doc, LS.Fields.Id));
        if (partnerType === LS.PartnerTypes.SalesOffice) {
            partnerLinkParams.set(LS.OtherUrlParams.isSalesOffice, 'true');
        } else if (isOpCo) {
            partnerLinkParams.set(LS.OtherUrlParams.aprCode, aprCode);
        }

        // Define card info
        const cardInfo = {
            partnerLink: `${this.config.locatorDetailLink}?${partnerLinkParams.toString()}`,
            partnerType: LS.getField(doc, LS.Fields.PartnerType),
            partnerName: LS.getField(doc, LS.Fields.Name) ?? LS.getField(doc, LS.Fields.SalesOffice.DisplayName) ?? LS.getField(doc, LS.Fields.SalesOffice.Name),
            bodyText: this.generateCardBodyText(doc),
            partnerEmail: LS.getField(doc, LS.Fields.PrimaryLocation.Email) ?? LS.getField(doc, LS.Fields.SalesOffice.Email),
            partnerPhone: LS.getField(doc, LS.Fields.Phone) ?? LS.getField(doc, LS.Fields.SalesOffice.MainPhone),
        };
        let subTypes = LS.getFieldAsArray(doc, LS.Fields.PartnerSubtype);

        // Certain information is replaced for operating companies
        if (isOpCo) {
            const location = await this.readLocationState();
            // Find nearest operating company that matches the APR code
            const opCoIdx = LS.getNearestOpCoIndex(doc, aprCode, location);

            cardInfo.partnerName = LS.getField(doc, LS.Fields.Distributor.Name, opCoIdx);
            cardInfo.partnerEmail = LS.getField(doc, LS.Fields.Distributor.Email, opCoIdx);
            cardInfo.partnerPhone = LS.getField(doc, LS.Fields.Distributor.Phone, opCoIdx);

            subTypes = [LS.getField(doc, LS.Fields.PartnerSubtype, opCoIdx)];
        }

        // Define how the logo is loaded
        let imgSrcPromise;
        if (isOpCo) {
            imgSrcPromise = LS.getOpCoLogo({
                apiData: doc,
                aprCode,
                parentLocationId: LS.getField(doc, LS.Fields.PrimaryLocation.Id),
                parentPartnerId: LS.getField(doc, LS.Fields.Id)
            });
        } else if (partnerType === 'Sales Office') {
            imgSrcPromise = this.config.salesOfficeLogoSrc;
        } else {
            imgSrcPromise = LS.getPartnerLogo(
                LS.getField(doc, LS.Fields.PrimaryLocation.Id),
                LS.getField(doc, LS.Fields.Id)
            );
        }

        return this.fillResultCard(cardInfo, subTypes, imgSrcPromise);
    };

    /**
     * @param {Record<string, string>} params
     * @param {string[]} subTypes
     * @param {string | Promise<string>} imgSrcPromise
     * @returns {HTMLElement}
     */
    fillResultCard = ({
        partnerLink, partnerType, partnerName, bodyText, partnerEmail, partnerPhone
    }, subTypes, imgSrcPromise) => {
        // Fill template element
        const card = createFilledElement(this.elements.resultCardTemplate, 'data-xt', {
            partnerLink,
            partnerType: LS.partnerTypeDisplayNameMap[partnerType] ?? partnerType,
            partnerName,
            bodyText,
            partnerEmail,
            partnerPhone
        });

        // Load logo asynchronously
        const imgEl = card.querySelector(this.selectors.resultLogo);
        (async () => {
            const imgSrc = await imgSrcPromise;
            if (imgSrc) {
                imgEl.src = imgSrc;
                imgEl.hidden = false;
                // Add error handler for 404 or other load failures
                imgEl.addEventListener('error', () => {
                    imgEl.hidden = true;
                });
            }
        })();

        // Add result type chips
        const partnerSubTypeDisplayNameMap = {
            'Is Var': 'IS VAR',
        };
        const subTypeChips = [...new Set(subTypes)]
            .map((subType) => partnerSubTypeDisplayNameMap[subType] ?? subType)
            .sort(new Intl.Collator().compare)
            .map((subType) => createFilledElement(this.elements.resultChipTemplate, 'data-xt', { partnerSubtype: subType }));
        card.querySelector(this.selectors.resultChipsContainer).prepend(...subTypeChips);

        // Add event listeners
        card.querySelector(this.selectors.resultIconPhone)?.addEventListener('click', LS.openContactDialog);
        card.querySelector(this.selectors.resultIconEmail)?.addEventListener('click', LS.openGatedForm);

        return card;
    };

    // ==========
    // UI State
    // ==========
    updateUiState = () => {
        const pageCount = Math.ceil(this.state.resultCount / this.state.numResults);
        const currentPageNum = Math.floor(this.state.resultStartIndex / this.state.numResults + 1);
        const firstResultOnCurrentPage = Math.min(this.state.resultCount, this.state.resultStartIndex + 1);
        const lastResultOnCurrentPage = Math.min(this.state.resultCount, this.state.resultStartIndex + this.state.numResults);

        // Update result counts
        FS.updateResultCounts(this, this.elements.paginationForm, {
            firstResultNum: firstResultOnCurrentPage,
            lastResultNum: lastResultOnCurrentPage,
            totalResults: this.state.resultCount,
            currentPageNum,
            pageCount,
        });

        // Update chips
        const hasChips = FS.updateChips(
            this.elements.filterForm,
            this.elements.filterChipTemplate,
            this.handleFilterForm,
            this.elements.filterChipsContainers,
            [this.config.locationSearchParam, this.config.querySearchParam],
        );

        // Hide/unhide mobile header bottom and reset buttons
        this.elements.filterMobileHeaderBottom.hidden = !hasChips;
        this.elements.filterResets.forEach((el) => {
            el.disabled = !hasChips;
        });

        // Sync desktop sort select
        this.elements.desktopSortSelect.value = new FormData(this.elements.filterForm).get(this.config.sortSearchParam);
        this.elements.desktopSortSelect.sync?.();
    };

    /**
     * @param {Record<string, string?>} params
     * @returns {HTMLElement}
     */
    createFilterChip = (params) => {
        const chip = createFilledElement(this.elements.filterChipTemplate, 'data-xt', params, { keepIfEmpty: true });
        chip.querySelector('button').addEventListener('click', this.handleChipClick);
        return chip;
    };

    /** @param {MouseEvent} ev */
    handleChipClick = (ev) => FS.handleChipClick(ev, this.elements.filterForm, this.handleFilterForm);

    // ==========
    // UI Interaction
    // ==========
    // Filter form
    /** @param {SubmitEvent} ev */
    handleFilterForm = (ev = null) => {
        ev?.preventDefault();
        this.state.resultStartIndex = 0;
        this.runSearch();
    };

    // Pagination
    handleResultPagination = LS.handlePagination({
        handlePrevButton: () => this.state.resultStartIndex -= this.state.numResults,
        handleNextButton: () => this.state.resultStartIndex += this.state.numResults,
        handleInput: (input) => this.state.resultStartIndex = (input - 1) * this.state.numResults,
        then: this.runSearch,
    });

    // Mobile Filter Modal
    showMobileFilter = () => {
        FS.showMobileFilter(this.elements.filterDialog);
        this.elements.tooltips.forEach((el) => FS.adjustTooltipPosition({ currentTarget: el }));
    };

    closeMobileFilter = () => FS.closeMobileFilter(this.elements.filterDialog);

    /** @param {Event} ev */
    handleDesktopSortSelect = (ev) => {
        FS.fillInput([...this.elements.filterForm.elements], this.config.sortSearchParam, ev.currentTarget.value, true);
        this.handleFilterForm();
    };

    // ==========
    // ADD EVENT LISTENERS
    // ==========
    addEventListeners = () => {
        // Location dialog
        this.elements.locationSelectButton.addEventListener('click', this.openLocationDialog);
        this.elements.locationDialogClose.addEventListener('click', this.closeLocationDialog);
        this.elements.locationDialog.addEventListener('click', (ev) => {
            if (!this.elements.locationDialogCard.contains(ev.target)) {
                this.closeLocationDialog();
            }
        });
        // Location autosuggest
        this.elements.locationSearchInput.addEventListener('input', this.handleLocationInput);
        this.elements.locationSearchInput.addEventListener('keydown', this.handleLocationKeys);
        this.elements.locationSearchInput.addEventListener('blur', this.handleLocationBlur);
        this.elements.locationAutosuggestList.addEventListener('click', this.handleAutosuggestClick);
        // Search & Filters
        this.elements.filterForm.addEventListener('submit', this.handleFilterForm);
        this.elements.filterForm.addEventListener('reset', () => window.setTimeout(this.handleFilterForm));
        FS.onFormControlChange(this.elements.filterForm, this.handleFilterForm);
        FS.addFacetListeners(this);
        this.elements.facetSearches.forEach((el) => el.addEventListener('input', FS.handleFacetSearch));
        // Tooltips
        this.elements.tooltips.forEach(FS.handleTooltip);
        // Synchronize the two sort selects (one for desktop, one for mobile)
        this.elements.desktopSortSelect.addEventListener('change', this.handleDesktopSortSelect);

        // Mobile Filter Dialog
        this.elements.filterMobileShow.addEventListener('click', this.showMobileFilter);
        this.elements.filterMobileClose.addEventListener('click', this.closeMobileFilter);
        this.elements.filterMobileSubmit.addEventListener('click', this.closeMobileFilter);
        // Pagination
        this.elements.paginationForm.addEventListener('submit', this.handleResultPagination);
    };

    // ==========
    // INITIALIZE COMPONENT
    // ==========
    connectedCallback() {
        window.addEventListener('load', async () => { // Wait for Adobe launch to run
            await this.runSearch(true);
            this.addEventListeners();
            this.dispatchEvent(new CustomEvent(`${componentName}__initialized`));
        });

        /* Listen for message that indicates that the location was successfully
           fetched via Akamai header and set as cookie. If user location was
           missing before, re-run the initial search with the populated the user location.
           The message is posted by Locator_Ping.js from Adobe Launch. */
        window.addEventListener('message', (ev) => {
            if (ev.data === 'piraLPingSuccess' && (this.state.userLocation.latitude == null || this.state.userLocation.longitude == null)) {
                this.runSearch(true);
            }
        });
    }
}

window.customElements.define(componentName, LocatorSearch);
